import { useFrame } from '@react-three/fiber'
import { useMemo, useRef } from 'react'
import * as THREE from 'three'
import { useShipStore } from '@/state/useShipStore'
import { ShipMesh } from './ShipMesh'
import { SHIP_TIERS } from './shipTypes'
import { createShipMaterial } from './shipShader'
import { sampleOceanHeight } from './oceanHeight'
import type { OwnedShip } from '@/state/useShipStore'

interface DockedShipsProps {
  sphereRadius: number
  sunPosition?: THREE.Vector3
}

/** Renders all owned ships that the player is NOT currently aboard */
export function DockedShips({ sphereRadius, sunPosition }: DockedShipsProps) {
  const ships = useShipStore((s) => s.ownedShips)
  const activeShipId = useShipStore((s) => s.activeShipId)

  const visibleShips = ships.filter((s) => s.id !== activeShipId)

  return (
    <>
      {visibleShips.map((ship) => (
        <DockedShipInstance
          key={ship.id}
          ship={ship}
          sphereRadius={sphereRadius}
          sunPosition={sunPosition}
        />
      ))}
    </>
  )
}

function DockedShipInstance({
  ship,
  sphereRadius,
  sunPosition,
}: {
  ship: OwnedShip
  sphereRadius: number
  sunPosition?: THREE.Vector3
}) {
  // Each docked ship gets its own GPU buoyancy material
  const material = useMemo(() => createShipMaterial(sphereRadius), [sphereRadius])

  const tierInfo = SHIP_TIERS[ship.tier]
  const smoothPitchRef = useRef(0)
  const smoothRollRef = useRef(0)

  useFrame((state, delta) => {
    const dt = Math.min(delta, 0.1)
    const time = state.clock.elapsedTime
    const u = material.uniforms
    u.uShipTheta.value = ship.theta
    u.uShipPhi.value = ship.phi
    u.uShipHeading.value = ship.heading

    // CPU buoyancy probes
    const sinPhi = Math.sin(ship.phi), cosPhi = Math.cos(ship.phi)
    const sinTheta = Math.sin(ship.theta), cosTheta = Math.cos(ship.theta)
    const cosH = Math.cos(ship.heading), sinH = Math.sin(ship.heading)

    const nx = sinPhi * sinTheta
    const ny = cosPhi
    const nz = sinPhi * cosTheta

    const fwdX = -(cosPhi * sinTheta) * cosH + cosTheta * sinH
    const fwdY = sinPhi * cosH
    const fwdZ = -(cosPhi * cosTheta) * cosH + (-sinTheta) * sinH

    const rightX = fwdY * nz - fwdZ * ny
    const rightY = fwdZ * nx - fwdX * nz
    const rightZ = fwdX * ny - fwdY * nx

    const hCenter = sampleOceanHeight(nx, ny, nz, time)

    const probeFwd = tierInfo.hullLength * 0.8
    const probeRight = tierInfo.hullWidth * 1.0
    const angFwd = probeFwd / sphereRadius
    let fpx = nx + fwdX * angFwd, fpy = ny + fwdY * angFwd, fpz = nz + fwdZ * angFwd
    const fLen = Math.sqrt(fpx * fpx + fpy * fpy + fpz * fpz)
    fpx /= fLen; fpy /= fLen; fpz /= fLen
    const hFwd = sampleOceanHeight(fpx, fpy, fpz, time)

    const angRight = probeRight / sphereRadius
    let rpx = nx + rightX * angRight, rpy = ny + rightY * angRight, rpz = nz + rightZ * angRight
    const rLen = Math.sqrt(rpx * rpx + rpy * rpy + rpz * rpz)
    rpx /= rLen; rpy /= rLen; rpz /= rLen
    const hRight = sampleOceanHeight(rpx, rpy, rpz, time)

    let targetPitch = Math.atan2(hCenter - hFwd, probeFwd)
    let targetRoll = Math.atan2(hCenter - hRight, probeRight)
    targetPitch = Math.max(-0.10, Math.min(0.10, targetPitch))
    targetRoll = Math.max(-0.10, Math.min(0.10, targetRoll))

    const smoothFactor = 1 - Math.exp(-4 * dt)
    smoothPitchRef.current += (targetPitch - smoothPitchRef.current) * smoothFactor
    smoothRollRef.current += (targetRoll - smoothRollRef.current) * smoothFactor

    u.uOceanHeight.value = hCenter + tierInfo.hullHeight * 0.2
    u.uPitch.value = smoothPitchRef.current
    u.uRoll.value = smoothRollRef.current

    if (sunPosition) {
      u.uSunDirection.value.copy(sunPosition).normalize()
      u.uSunHeight.value = sunPosition.y / sunPosition.length()
    }

    const camPos = state.camera.position
    const camLen = camPos.length()
    if (camLen > 0) {
      u.uCameraDir.value.set(camPos.x / camLen, camPos.y / camLen, camPos.z / camLen)
      u.uCameraAlt.value = camLen - sphereRadius
    }
  })

  return <ShipMesh tier={ship.tier} material={material} />
}
